import {
  chartOptionsStableBalance,
  FITTED_BALANCE_GRAPH_ID,
  getForecastPlotBandObj,
  getForecastStartPlotLineObj as getCalibrationEndPlotLineObj,
  getLastObservationDatePlotLineObj,
  NON_STABLE_AMOUNT_GRAPH_ID,
  NON_STABLE_PERCENT_GRAPH_ID,
  STABLE_BALANCE_GRAPH_ID,
  TOTAL_BALANCE_GRAPH_ID,
  VOL_ADJ_BALANCE_GRAPH_ID
} from '@components/stable-balance/stable-balance-chart-options';
import { IStableBalanceCalibration, IStableBalanceSeries } from '@models/stable-balance/stable-balance-calibration';
import { getDateString, getFirstDayOfMonth, isValidDate } from '@shared/dates';
import { IExportChartOption } from '../export.config';
import { filterStableBalanceSeries } from './helpers';

export const stableBalanceChartJSON = (calibration: IStableBalanceCalibration, exportChartOption: IExportChartOption) => {
  let chartJSON: any = {
    ...chartOptionsStableBalance(),
    exporting: {
      sourceWidth: exportChartOption.width,
      sourceHeight: exportChartOption.height,
      allowHTML: true
    }
  };

  chartJSON.series = getStableBalanceSeries(chartJSON, calibration);
  chartJSON = setAxisLimits(chartJSON, calibration);
  chartJSON = setPlots(chartJSON, calibration);
  chartJSON = populateAnnotations(
    chartJSON,
    new Date(calibration.LastObservationDate),
    new Date(calibration.CalibrationEndDate),
    calibration.FittedBalances
  );

  delete chartJSON.xAxis.tickInterval;
  return chartJSON;
};

export const populateAnnotations = (chart: any, lastObservationDate: Date, forecastDate: Date, fittedBalances: IStableBalanceSeries[]) => {
  const annotations = [
    {
      labelOptions: {
        backgroundColor: 'rgba(0, 0, 0, 0)',
        borderColor: 'rgba(0, 0, 0, 0)',
        shape: 'connector',
        verticalAlign: 'top',
        y: 15,
        style: {
          fontSize: '12px',
          writingMode: 'vertical-rl'
        }
      },
      labels: [
        {
          point: {
            xAxis: 0,
            yAxis: 1,
            x: lastObservationDate.getTime(),
            y: 255
          },
          style: {
            fontSize: '12px',
            // writingMode: 'vertical-rl',
            // webkitWritingMode: 'vertical-rl',
            // msWritingMode: 'vertical-rl',
            writingMode: 'tb-rl'
          },
          text: 'Last Obs.'
        },
        {
          point: {
            xAxis: 0,
            yAxis: 1,
            x: forecastDate.getTime(),
            y: 90
          },
          text: 'Calibration End'
        }
      ]
    }
  ];

  chart.annotations = annotations;

  return chart;
};

const setPlots = (chartJSON, calibration: IStableBalanceCalibration) => {
  chartJSON.xAxis.plotLines = [];
  chartJSON.xAxis.plotBands = [];

  const calibrationEndDate = new Date(calibration.CalibrationEndDate);
  const lastObservationDate = new Date(calibration.LastObservationDate);
  const forecastEndDate = new Date(calibration.ForecastEndDate);

  // Calibration End Date Line
  if (isValidDate(calibrationEndDate)) {
    chartJSON = setCalibrationEndDateLine(chartJSON, calibrationEndDate);

    // Last Observation Date Line
    if (isValidDate(lastObservationDate) && getDateString(calibration.LastObservationDate) !== getDateString(calibrationEndDate)) {
      chartJSON = setLastObservationDateLine(chartJSON, lastObservationDate);

      // Forecast Band
      chartJSON = setForecastBand(chartJSON, calibrationEndDate, forecastEndDate);
    }
  }

  return chartJSON;
};

const setCalibrationEndDateLine = (chartJSON, date: Date) => {
  chartJSON.xAxis.plotLines.push(getCalibrationEndPlotLineObj(date));
  return chartJSON;
};

const setLastObservationDateLine = (chartJSON, date: Date) => {
  chartJSON.xAxis.plotLines.push(getLastObservationDatePlotLineObj(date));
  return chartJSON;
};

const setForecastBand = (chartJSON, calibrationEndDate: Date, forecastEndDate: Date) => {
  try {
    if (isValidDate(calibrationEndDate) && isValidDate(calibrationEndDate)) {
      chartJSON.xAxis.plotBands.push(getForecastPlotBandObj(calibrationEndDate, forecastEndDate));
    }
  } catch (err) {
    console.error('[setForecastBand]', err);
  }
  return chartJSON;
};

const setAxisLimits = (chartJSON: any, calibration: IStableBalanceCalibration) => {
  // Horizontal - X Axe
  const startDate = new Date(calibration.CalibrationStartDate);
  const endDate = new Date(calibration.ForecastEndDate);
  if (isValidDate(startDate) && isValidDate(endDate)) {
    chartJSON.xAxis.min = startDate.getTime();
    chartJSON.xAxis.max = endDate.getTime();
  }

  // Vertical - Y Axe - Right
  const { positions, dataMin } = createYAxisValues(chartJSON, calibration);
  chartJSON.yAxis[1].softMin = 0;
  chartJSON.yAxis[1].softMax = 100;
  chartJSON.yAxis[1].min = dataMin;
  chartJSON.yAxis[1].max = 100;
  chartJSON.yAxis[1].tickPositions = positions;

  return chartJSON;
};

const getStableBalanceSeries = (chartJSON, calibration: IStableBalanceCalibration) => {
  return [
    // -- Total Balance ($)
    buildSerie(
      chartJSON,
      TOTAL_BALANCE_GRAPH_ID,
      calibration.TotalBalances.filter(bal =>
        filterStableBalanceSeries(
          bal,
          new Date(calibration.CalibrationStartDate),
          new Date(calibration.CalibrationEndDate),
          new Date(calibration.ForecastStartDate),
          new Date(calibration.ForecastEndDate)
        )
      ),
      calibration,
      false
    ),
    // -- Fitted Balance ($)
    buildSerie(
      chartJSON,
      FITTED_BALANCE_GRAPH_ID,
      calibration.FittedBalances.filter(bal =>
        filterStableBalanceSeries(
          bal,
          new Date(calibration.CalibrationStartDate),
          new Date(calibration.CalibrationEndDate),
          new Date(calibration.ForecastStartDate),
          new Date(calibration.ForecastEndDate)
        )
      ),
      calibration,
      false
    ),
    // -- Volatility Adjusted ($)
    buildSerie(
      chartJSON,
      VOL_ADJ_BALANCE_GRAPH_ID,
      calibration.VolatilityAdjustments.filter(bal =>
        filterStableBalanceSeries(
          bal,
          new Date(calibration.CalibrationStartDate),
          new Date(calibration.CalibrationEndDate),
          new Date(calibration.ForecastStartDate),
          new Date(calibration.ForecastEndDate)
        )
      ),
      calibration,
      false
    ),
    // -- Stable Balance ($)
    buildSerie(
      chartJSON,
      STABLE_BALANCE_GRAPH_ID,
      calibration.StableBalances.filter(bal =>
        filterStableBalanceSeries(
          bal,
          new Date(calibration.CalibrationStartDate),
          new Date(calibration.CalibrationEndDate),
          new Date(calibration.ForecastStartDate),
          new Date(calibration.ForecastEndDate)
        )
      ),
      calibration,
      false
    ),
    // -- Non-Stable Balance Amount - Non-Stable ($)
    buildSerie(
      chartJSON,
      NON_STABLE_AMOUNT_GRAPH_ID,
      calibration.NonStableBalances.filter(bal =>
        filterStableBalanceSeries(
          bal,
          new Date(calibration.CalibrationStartDate),
          new Date(calibration.CalibrationEndDate),
          new Date(calibration.ForecastStartDate),
          new Date(calibration.ForecastEndDate)
        )
      ),
      calibration,
      false
    ),
    // -- Non-Stable Balance Percent- Non-Stable (%)
    buildSerie(
      chartJSON,
      NON_STABLE_PERCENT_GRAPH_ID,
      calibration.NonStablePercents.filter(bal =>
        filterStableBalanceSeries(
          bal,
          new Date(calibration.CalibrationStartDate),
          new Date(calibration.CalibrationEndDate),
          new Date(calibration.ForecastStartDate),
          new Date(calibration.ForecastEndDate)
        )
      ),
      calibration,
      true
    )
  ];
};

const buildSerie = (origChartJSON, origSerieId: string, src: any, calibration: IStableBalanceCalibration, isPercent: boolean = false) => {
  try {
    const data = (src || []).map(item => [
      getFirstDayOfMonth(new Date(item.PeriodEndDate)).getTime(),
      isPercent ? item.Balance * 100 : item.Balance
    ]);
    return {
      ...origChartJSON.series.find(s => s.id === origSerieId),
      data
    };
  } catch (err) {
    console.error(`Unable to add Total Balance serie to Stable Balance Chart`, err);
  }
};

const createYAxisValues = (chartJSON: any, calibration: IStableBalanceCalibration) => {
  const yAxisSeries = chartJSON.series.filter(s => s.yAxis === 1);
  let dataMin = Infinity;

  yAxisSeries.forEach(series => {
    series.data.forEach(item => {
      if (item[1] < dataMin) {
        dataMin = item[1];
      }
    });
  });

  const positions = [];
  const padding = 0;
  const max = 100;
  let tick = -100;
  if (dataMin !== Infinity) {
    const increment = Math.ceil((100 - dataMin) / 10) > 10 ? 25 : 10;
    for (tick; tick - increment <= max; tick += increment) {
      if (tick <= max && tick + increment + padding > dataMin) {
        positions.push(tick);
      }
    }
  }
  return { positions, dataMin };
};
